export const normalizeDigits = (value) => String(value ?? '').replace(/\D/g, '');

export const toNumOrNull = (v) => {
  // Number(null) and Number('') are 0, which would read as a critical vital.
  if (v === null || v === undefined || v === '' || v === '--') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Vitals ranges mirror the vitals-chat results page (tikcare-vitals-react,
// VitalsMentalHealth.js + vital_result_printer.js). Keep the two in sync.
// The scan SDK reports an invalid reading as -1 and vitals-chat shows "--" for
// anything <= 0, so treat those as unavailable rather than critical.
const isMissing = (v) => v === null || v === undefined || v <= 0;

export const getHrStatus = (v) => {
  if (isMissing(v)) return 'unavailable';
  if (v <= 55) return 'critical';
  if (v >= 130) return 'critical';
  if (v < 60 || v > 100) return 'abnormal';
  return 'normal';
};

export const getSpo2Status = (v) => {
  if (isMissing(v)) return 'unavailable';
  if (v <= 88) return 'critical';
  if (v > 100) return 'critical';
  if (v >= 95 && v <= 100) return 'normal';
  return 'abnormal';
};

export const getBpStatus = (rawSbp, rawDbp) => {
  const sbp = isMissing(rawSbp) ? null : rawSbp;
  const dbp = isMissing(rawDbp) ? null : rawDbp;
  if (sbp === null && dbp === null) return 'unavailable';
  if (sbp !== null && sbp >= 180) return 'critical';
  if (dbp !== null && dbp >= 120) return 'critical';

  const sbpAbnormal = sbp !== null && (sbp < 90 || sbp >= 120);
  const dbpAbnormal = dbp !== null && (dbp < 60 || dbp >= 80);
  if (sbpAbnormal || dbpAbnormal) return 'abnormal';

  if ((sbp !== null && sbp >= 90 && sbp <= 119) || (dbp !== null && dbp >= 60 && dbp <= 79)) {
    return 'normal';
  }

  return 'abnormal';
};

export const getHrvStatus = (v) => {
  if (isMissing(v)) return 'unavailable';
  if (v < 20) return 'abnormal';
  return 'normal';
};

export const getRrStatus = (v) => {
  if (isMissing(v)) return 'unavailable';
  if (v >= 35) return 'critical';
  if (v < 12 || v > 20) return 'abnormal';
  return 'normal';
};

export const getSiStatus = (v) => {
  if (v === null || v === '--') return 'unavailable';
  const num = parseFloat(v);
  if (Number.isNaN(num)) return 'unavailable';

  if (num < 0) return 'unavailable';
  if (num < 50) return 'Low';
  if (num < 200) return 'Normal';
  if (num < 350) return 'Moderate';
  if (num < 500) return 'High';
  return 'Very High';
};

// Stress Index severity, matching how vitals-chat colours the card:
// Low/Normal = normal, Moderate = abnormal, High/Very High = critical.
export const isSiCritical = (status) => status === 'High' || status === 'Very High';
export const isSiAbnormal = (status) => status === 'Moderate';

// Chip label for a stress index: 'High' / 'Moderate' when it should be flagged, else null.
export const getStressLabel = (v) => {
  const status = getSiStatus(v);
  if (isSiCritical(status)) return 'High';
  if (isSiAbnormal(status)) return 'Moderate';
  return null;
};

export const clampScore = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(5, n));
};

export const scoreLabel = (v, max = 5) => {
  if (v === null || v === undefined || v === '' || v === '--') return 'Not Available';
  const num = Number(v);
  // The SDK reports -1 when it could not compute an indicator.
  if (Number.isNaN(num) || num < 0) return 'Not Available';
  const percentage = (num / max) * 100;
  if (percentage > 66) return 'Excellent';
  if (percentage > 33) return 'Good';
  return 'Poor';
};

export const getVitalFlagsFromScan = (scan) => {
  if (!scan) return { critical: [], abnormal: [] };

  const hr = toNumOrNull(scan.heart_rate);
  const spo2 = toNumOrNull(scan.oxygen_saturation);
  const rr = toNumOrNull(scan.respiration_rate);
  const hrv = toNumOrNull(scan.hr_variability);
  const sbp = toNumOrNull(scan.sbp);
  const dbp = toNumOrNull(scan.dbp);

  const vitals = [
    { name: 'Heart Rate', status: getHrStatus(hr) },
    { name: 'Blood Pressure', status: getBpStatus(sbp, dbp) },
    { name: 'Blood Oxygen', status: getSpo2Status(spo2) },
    { name: 'Respiratory Rate', status: getRrStatus(rr) },
    { name: 'HRV', status: getHrvStatus(hrv) },
  ];

  return {
    critical: vitals.filter((v) => v.status === 'critical').map((v) => v.name),
    abnormal: vitals.filter((v) => v.status === 'abnormal').map((v) => v.name),
  };
};

export const getWellnessFlagsFromScan = (scan) => {
  if (!scan) return { poor: [] };

  const metrics = [
    { name: 'Activity', value: scan.activity },
    { name: 'Sleep', value: scan.sleep },
    { name: 'Equilibrium', value: scan.equilibrium },
    { name: 'Metabolism', value: scan.metabolism },
    { name: 'Resilience', value: scan.health },
    { name: 'Relaxation', value: scan.relaxation },
  ];

  const poor = metrics
    .filter((m) => scoreLabel(m.value) === 'Poor')
    .map((m) => m.name);

  return { poor };
};